// Postventa classification taxonomy — SHAPES and mechanism only
// (ADR-0129). The CONTENT (the RC/EL/especialidad/partida/TF/PR
// catalogs, the tie-break rules, the expected-combinations matrix)
// lives in `packages/postventa-taxonomy`, which apps inject the same
// way they inject the recipe registry — the engine never imports the
// content package (same inversion as RecipeRegistry, ADR-0019).
//
// Why the split: the catalog is a versioned, org-independent artifact
// whose rows must survive a quarterly revision without touching the
// engine, and whose codes must NEVER ride the recipe payload to the
// PWA (the propietario never sees taxonomy codes — spec invariant 8;
// `recipeMeResponseSchema` derives by omit(), so anything on the
// recipe reaches the client by default).
import { z } from "zod";
export const taxonomyRecintoSchema = z.object({
    codigo: z.string().regex(/^RC-\d{2}$/),
    nombre: z.string().min(1),
    ambito: z.enum(["privado", "comun"]),
    /** §4 del spec: recintos que se repiten dentro de una unidad. La
     *  resolución de instancias (Tipología/Slot/Instancia/Alias) quedó
     *  DIFERIDA por ruling del founder 2026-09-01 (ADR-0118); el flag se
     *  conserva porque es contenido del catálogo, no mecanismo. */
    requiereInstancia: z.boolean(),
});
export const taxonomyElementoSchema = z.object({
    codigo: z.string().regex(/^EL-\d{2}$/),
    nombre: z.string().min(1),
});
export const taxonomyPartidaSchema = z.object({
    /** e.g. "IMP-07". Prefix always matches its especialidad's código. */
    codigo: z.string().regex(/^[A-Z]{3}-\d{2}$/),
    nombre: z.string().min(1),
});
export const taxonomyEspecialidadSchema = z.object({
    /** e.g. "IMP", "OTR". The "who repairs" axis (spec §2 regla de oro:
     *  la categoría es la causa, no el síntoma). */
    codigo: z.string().regex(/^[A-Z]{3}$/),
    nombre: z.string().min(1),
    /**
     * One-line es-CL help for a human choosing between candidates (P9's
     * radiogroup, D3/D5's `.cand-block`): what this especialidad actually
     * repairs, worded so two neighbours read as different. STATIC catalog
     * copy, reviewed and versioned — never model prose. Letting the model
     * emit a contrastive "ayuda" would put free text through the sanitizer
     * onto the wire at exactly the surface where the human's pick is the
     * legal act. Adding this field does NOT bump the catalog version: the
     * version identifies the code space for decoding historical rows, not
     * the copy (ADR-0119; #0606).
     */
    ayuda: z.string().min(1).max(160),
    partidas: z.array(taxonomyPartidaSchema).min(1),
});
export const taxonomyTipoFallaSchema = z.object({
    codigo: z.string().regex(/^TF-\d{2}$/),
    nombre: z.string().min(1),
    criterio: z.string().min(1),
    /** TF-04 carries agua_activa; TF-01 carries ancho_mm. Declared on
     *  the catalog so the conditional-field rule is data, not code. */
    campoAdicional: z.enum(["agua_activa", "ancho_mm"]).optional(),
});
export const taxonomyProcedenciaSchema = z.object({
    codigo: z.string().regex(/^PR-\d{2}$/),
    nombre: z.string().min(1),
    criterio: z.string().min(1),
    efecto: z.string().min(1),
});
/** One tie-break rule (spec §9 / xlsx "Reglas de desempate"). The array
 *  in the content package is the SINGLE source of truth for both the
 *  human training doc (generated, CI-checked) and the classifier
 *  prompt — a rule added there lands in both in the same commit. */
export const taxonomyReglaDesempateSchema = z.object({
    id: z.string().regex(/^DES-\d{2}$/),
    caso: z.string().min(1),
    etiquetaAmbigua: z.string().min(1),
    categoriaCorrecta: z.string().min(1),
    porQue: z.string().min(1),
});
export const taxonomyCatalogSchema = z.object({
    /** The string stamped on every observation row (spec §7) — see
     *  TAXONOMY_VERSION_CURRENT in the content package. NEVER
     *  client-supplied — the server stamps it from that constant at
     *  insert time. */
    version: z.string().regex(/^\d+\.\d+-mvp$/),
    recintos: z.array(taxonomyRecintoSchema).min(1),
    elementos: z.array(taxonomyElementoSchema).min(1),
    especialidades: z.array(taxonomyEspecialidadSchema).min(1),
    tiposFalla: z.array(taxonomyTipoFallaSchema).min(1),
    procedencias: z.array(taxonomyProcedenciaSchema).min(1),
    reglasDesempate: z.array(taxonomyReglaDesempateSchema),
    /** Expected especialidad × tipo-de-falla combinations ("Matriz
     *  sugerida"). RANKING AID ONLY — spec §8/invariant 7: the matrix
     *  orders classifier candidates and dashboard suggestions; it NEVER
     *  blocks a registration, in any form or route. There is no helper
     *  here that rejects on a miss, on purpose. */
    matrizEsperada: z.record(z.string(), z.array(z.string())),
});
// ---------------------------------------------------------------------------
// Lookups + validation (mechanism, engine-owned)
export function findTaxonomyEspecialidad(catalog, codEspecialidad) {
    return catalog.especialidades.find((e) => e.codigo === codEspecialidad);
}
export function findTaxonomyPartida(catalog, codPartida) {
    for (const especialidad of catalog.especialidades) {
        const partida = especialidad.partidas.find((p) => p.codigo === codPartida);
        if (partida)
            return { especialidad, partida };
    }
    return undefined;
}
/** True when the matriz lists (especialidad, tipoFalla) as an expected
 *  combination. Consumers may use this to SORT (expected first) —
 *  never to filter or reject (spec invariant 7). An unexpected
 *  combination the human confirms is the matrix-revision signal, not
 *  an error. */
export function isExpectedCombination(catalog, codEspecialidad, codTipoFalla) {
    return catalog.matrizEsperada[codEspecialidad]?.includes(codTipoFalla) ?? false;
}
/**
 * Membership + coherence validation for a classification write
 * (core-api's POST /findings/:id/classification). Zod at the wire
 * validates FORMAT; this validates MEMBERSHIP against the catalog the
 * row was (or is being) stamped with, plus the cross-field rules the
 * spec makes data-dependent:
 *   - every provided code exists in its catalog list;
 *   - codPartida belongs to codEspecialidad when both are present;
 *   - aguaActiva only with the tipo de falla that declares it (TF-04);
 *   - anchoMm only with the tipo de falla that declares it (TF-01).
 * Deliberately ABSENT: any check against matrizEsperada — the matrix
 * ranks, never blocks (spec invariant 7).
 */
export function validateTaxonomyClassification(catalog, input) {
    const issues = [];
    const especialidad = input.codEspecialidad != null
        ? findTaxonomyEspecialidad(catalog, input.codEspecialidad)
        : undefined;
    if (input.codEspecialidad != null && !especialidad) {
        issues.push(`unknown especialidad "${input.codEspecialidad}"`);
    }
    if (input.codPartida != null) {
        const found = findTaxonomyPartida(catalog, input.codPartida);
        if (!found) {
            issues.push(`unknown partida "${input.codPartida}"`);
        }
        else if (input.codEspecialidad != null &&
            found.especialidad.codigo !== input.codEspecialidad) {
            issues.push(`partida "${input.codPartida}" belongs to "${found.especialidad.codigo}", not "${input.codEspecialidad}"`);
        }
    }
    const tipoFalla = input.codTipoFalla != null
        ? catalog.tiposFalla.find((t) => t.codigo === input.codTipoFalla)
        : undefined;
    if (input.codTipoFalla != null && !tipoFalla) {
        issues.push(`unknown tipo de falla "${input.codTipoFalla}"`);
    }
    if (input.codRecinto != null && !catalog.recintos.some((r) => r.codigo === input.codRecinto)) {
        issues.push(`unknown recinto "${input.codRecinto}"`);
    }
    if (input.codElemento != null && !catalog.elementos.some((e) => e.codigo === input.codElemento)) {
        issues.push(`unknown elemento "${input.codElemento}"`);
    }
    if (input.codProcedencia != null &&
        !catalog.procedencias.some((p) => p.codigo === input.codProcedencia)) {
        issues.push(`unknown procedencia "${input.codProcedencia}"`);
    }
    if (input.aguaActiva != null) {
        if (!tipoFalla || tipoFalla.campoAdicional !== "agua_activa") {
            issues.push(`aguaActiva only applies to the tipo de falla that declares it (got "${input.codTipoFalla ?? "none"}")`);
        }
    }
    if (input.anchoMm != null) {
        if (!tipoFalla || tipoFalla.campoAdicional !== "ancho_mm") {
            issues.push(`anchoMm only applies to the tipo de falla that declares it (got "${input.codTipoFalla ?? "none"}")`);
        }
        if (typeof input.anchoMm === "number" && input.anchoMm <= 0) {
            issues.push("anchoMm must be positive");
        }
    }
    return issues;
}
/**
 * El recinto que un propietario puede reportar es SIEMPRE privado: el
 * canal `reporte_propietario` habla de su unidad, no del edificio, y un
 * recinto común reportado por esa vía dirige una orden de reparación a
 * un espacio que no es del reportante (minuta 04).
 *
 * Deliberadamente SEPARADO de `validateTaxonomyClassification`: esa
 * función la comparte `/findings/:id/classification`, donde un
 * supervisor SÍ puede clasificar legítimamente un hallazgo en un recinto
 * común. El ámbito no es una regla del catálogo, es una regla del CANAL.
 *
 * Devuelve issues, no boolean, para componer con el array que
 * `validateTaxonomyClassification` ya devuelve. Un código desconocido NO
 * se reporta acá: eso ya es trabajo de esa función, y duplicar el issue
 * haría que el 422 hablara dos veces del mismo campo.
 */
export function validateRecintoPrivado(catalog, codRecinto) {
    if (codRecinto == null)
        return [];
    const recinto = catalog.recintos.find((r) => r.codigo === codRecinto);
    // Código desconocido: lo reporta `validateTaxonomyClassification`.
    if (!recinto)
        return [];
    if (recinto.ambito !== "privado") {
        return [
            `recinto "${codRecinto}" is an área común and cannot be reported through the propietario channel`,
        ];
    }
    return [];
}
/**
 * Los recintos que `opciones` puede enumerar en el canal del
 * propietario. La MISMA fuente que el rechazo de arriba — una lista
 * filtrada por un helper y un rechazo escrito a mano son exactamente
 * cómo la UI y el servidor terminan discrepando.
 */
export function recintosPrivados(catalog) {
    return catalog.recintos.filter((r) => r.ambito === "privado");
}
// ---------------------------------------------------------------------------
// Derived severity (spec §5 — ADR-0129 §4)
export const DERIVED_SEVERITIES = ["S1", "S2", "S3", "S4"];
/**
 * severidad = 'S1' if P1 else ('S2' if P2 else ('S3' if P3 else 'S4')).
 *
 * THE single writer of `findings.derived_severity` (fitness:
 * derived-severity-single-writer). Pure function of the three
 * booleans — the model never emits S-codes, no human types one, and
 * the DB CHECK (migration 0051) re-asserts this CASE on every row that
 * carries the booleans. Hard rule (spec invariant 3): gas or exposed
 * live wiring forces P1 upstream — callers apply that override to the
 * BOOLEANS before calling this, never to the result.
 */
export function deriveObservationSeverity(p1Risk, p2FunctionOrProgression, p3MinorFunctional) {
    if (p1Risk)
        return "S1";
    if (p2FunctionOrProgression)
        return "S2";
    if (p3MinorFunctional)
        return "S3";
    return "S4";
}
/**
 * Partial-knowledge derivation — the SINGLE home of the short-circuit
 * rule for writers whose booleans may be unresolved (code-reviewer
 * finding, ola 2): a forced/true P1 derives S1 no matter what P2/P3
 * say (they are irrelevant by the spec's own ladder), true P2 with
 * unknown P3 derives S2, and S4 — the only level that needs all three
 * answers — derives only when all three are resolved false. Anything
 * else is genuinely unknown → null. Keeping this here (not inlined at
 * a route) is what stops the ladder living triplicated across engine,
 * DB CHECK and handlers.
 */
export function deriveObservationSeverityPartial(p1Risk, p2FunctionOrProgression, p3MinorFunctional) {
    if (p1Risk === true)
        return "S1";
    if (p1Risk !== false)
        return null;
    if (p2FunctionOrProgression === true)
        return "S2";
    if (p2FunctionOrProgression !== false)
        return null;
    if (p3MinorFunctional === true)
        return "S3";
    if (p3MinorFunctional !== false)
        return null;
    return "S4";
}
/**
 * Default plazo (calendar days — «días corridos», never «días hábiles»)
 * a contractor gets when an observation is assigned without an explicit
 * plazo: Crítica 3, Mayor 5, Menor 7, Estética 10 (founder decision,
 * listado de observaciones). THE single source for both the server
 * (POST /findings/assign, /findings/assign-batch) and the dashboard
 * drawer's pre-filled value.
 */
export const DEFAULT_PLAZO_DIAS_BY_SEVERITY = {
    S1: 3,
    S2: 5,
    S3: 7,
    S4: 10,
};
/** Severity used when no observation in the group carries one
 *  (unclassified legacy rows): the middle ground, Menor. */
export const DEFAULT_PLAZO_FALLBACK_SEVERITY = "S3";
/**
 * The default plazo for a GROUP of observations: the one of its highest
 * severity (S1 outranks S4), so the most urgent item sets the deadline
 * for the whole batch. Unknown severities are ignored; an all-unknown
 * group falls back to DEFAULT_PLAZO_FALLBACK_SEVERITY.
 */
export function defaultPlazoDiasFor(severities) {
    let highest = null;
    for (const s of severities) {
        // Un valor fuera del catálogo (dato viejo, cast sin validar) no gana:
        // con indexOf -1 le ganaría a S1 e indexaría la tabla con undefined.
        if (s == null || !DERIVED_SEVERITIES.includes(s))
            continue;
        if (highest === null || DERIVED_SEVERITIES.indexOf(s) < DERIVED_SEVERITIES.indexOf(highest)) {
            highest = s;
        }
    }
    return DEFAULT_PLAZO_DIAS_BY_SEVERITY[highest ?? DEFAULT_PLAZO_FALLBACK_SEVERITY];
}
/** The deterministic half of spec invariant 3("gas o tensión expuesta
 *  ⇒ P1, sin excepción"): GAS-anything, and ELE combined with the
 *  humedad/filtración/fuga tipo de falla (the "tablero mojado" star
 *  case). The general exposed-live-wiring case (e.g. ELE + TF-03) is
 *  NOT code-derivable and stays a classifier-prompt rule plus human
 *  review — stated in ADR-0129 rather than implied complete. */
export function p1ForcedByRule(codEspecialidad, codTipoFalla) {
    if (codEspecialidad === "GAS")
        return "GAS";
    if (codEspecialidad === "ELE" && codTipoFalla === "TF-04")
        return "ELE_TF04";
    return null;
}
// ---------------------------------------------------------------------------
// Confidence gate (spec invariant 10 — ADR-0129 §5, #0604)
/**
 * The SINGLE home of the review threshold: `confianza_ia` below this
 * blocks report emission until a human review stamps
 * `confianza_review_by/_at` (core-api enforces at POST /reports/:id/sign
 * with a 409 naming the observations; NULL confidence fails closed for
 * classified rows). Always a float compared at read time — never
 * materialized into a boolean column.
 */
export const CONFIANZA_IA_UMBRAL_REVISION = 0.75;
/**
 * Evidence clamp for the classifier's self-reported confidence
 * (#0604): without verified photo evidence the proposal can never
 * assert more than this, no matter what the model claims.
 */
export const CONFIANZA_SIN_EVIDENCIA_MAX = 0.7;
/**
 * Procedencia por eje (#0646, ruling del founder «C por enunciado»).
 *
 * El discriminador del carril declarativo NO es el nombre del eje sino
 * el enunciado: el gold set de #0647 ya falsificó la partición por eje
 * en las dos direcciones (LEX-B-05 «es en el baño, no te sabría decir
 * cuál de los dos» deja tres recintos aceptables; LEX-B-03 «el techo del
 * living, mirando desde adentro» cierra el elemento con una sola fila).
 * Por eso la fuente la declara el modelo POR HALLAZGO y el gate la
 * consume como dato, en vez de leerla de una constante cableada.
 *
 * - `declarado`: las palabras del humano alcanzan por sí solas para
 *   elegir exactamente una fila del catálogo.
 * - `inferido`: salió de la foto, del contexto o de una regla de
 *   desempate.
 */
export const FUENTES_EJE = ["declarado", "inferido"];
/**
 * Quién responde por el valor que un eje acaba de recibir, declarado por
 * el CLIENTE en `POST /findings/:id/classification` y contrastado por el
 * servidor contra su propio registro de corridas (#0646, ruling «C por
 * enunciado»).
 *
 * Es el hermano humano de `FUENTES_EJE`, y son cosas distintas:
 * `FuenteEje` dice de dónde sacó el MODELO el código (`declarado` |
 * `inferido`); esto dice qué hizo la PERSONA con él.
 *
 * - `confirmado_ia`: el supervisor ratificó el código que el modelo
 *   propuso para ESE enunciado. Sólo es aceptable cuando el registro del
 *   servidor respalda las tres cosas a la vez — que alguna corrida de
 *   este hallazgo propuso ese mismo código, que lo marcó `declarado`, y
 *   que la cita literal sobrevivió la verificación del sanitizador.
 * - `elegido_humano`: la persona eligió el valor por su cuenta; no hay
 *   propuesta que contrastar y el servidor no pide ninguna.
 *
 * NO se llama `procedencia` ni `ejeProcedencia` a propósito: ese nombre
 * ya pertenece a `cod_procedencia`, el eje que ninguna máquina escribe
 * (fitness:no-automated-writer-on-procedencia). Reusarlo acá haría que
 * un grep por «procedencia» mezclara dos invariantes sin relación.
 */
export const EJE_FUENTE_CODES = ["confirmado_ia", "elegido_humano"];
/**
 * The ONE implementation of the attribution predicate. Both providers
 * call it from the parse layer they share, so the mock and the real
 * model exercise the same clamp — the dashboard's local stack runs
 * `AI_PROVIDER=mock`, and a provider-local predicate is how local QA
 * ends up testing a different gate than production.
 *
 * Fail-closed by construction under `verified_indices`: a missing
 * `photoIndices`, an empty one, or one whose every index is out of range
 * all resolve to `attributed: false`, which holds the clamp at
 * `CONFIANZA_SIN_EVIDENCIA_MAX`. None of them is a schema error — the
 * model gave a usable answer whose attribution is missing, and the
 * product decision is to degrade the routing, not abort the call.
 *
 * Out-of-range indices are dropped, not treated as omission, when at
 * least one in-range index remains: discarding a real citation over one
 * hallucinated index is disproportionate (analyze-block already documents
 * a model returning `photoIndices: [99]`).
 */
export function resolveEvidenceAttribution(policy, photoIndices, photoCount) {
    const inRange = (photoIndices ?? []).filter((i) => Number.isInteger(i) && i >= 0 && i < photoCount);
    if (policy.kind === "any_photo") {
        return { attributed: photoCount > 0, matchedVerifiedIndices: [] };
    }
    const verified = new Set(policy.verifiedPhotoIndices);
    const matched = inRange.filter((i) => verified.has(i));
    return { attributed: matched.length > 0, matchedVerifiedIndices: matched };
}
/**
 * What the supervisor confirmed in the field before asking for a
 * reclassification (P5/P6 pickers; handoff §Interactions). Typed here,
 * not as loose strings on ai-svc's `AreaInput`, for the same reason
 * `AnalyzedFinding.taxonomy` is engine-typed: postventa vocabulary on a
 * shared provider type is gated on the primitive, never on the slug.
 *
 * These inform the LOCATION and FAILURE-TYPE axes only. They never
 * decide `codEspecialidad`, and the prompt says so explicitly — otherwise
 * the model reads three confirmed codes as corroboration and returns a
 * high `confianza`, which silences the confidence DROP handoff rule 5
 * requires to stay observable.
 */
export const anchoBandSchema = z.enum(["lt1", "1to3", "gt3"]);
export const taxonomyHintsSchema = z
    .object({
    codRecinto: z
        .string()
        .regex(/^RC-\d{2}$/)
        .nullable(),
    codElemento: z
        .string()
        .regex(/^EL-\d{2}$/)
        .nullable(),
    codTipoFalla: z
        .string()
        .regex(/^TF-\d{2}$/)
        .nullable(),
    aguaActiva: z.boolean().nullable(),
    anchoMm: z.number().int().nonnegative().max(500).nullable(),
    // The PWA collects the crack width as a BAND («nadie lleva pie de
    // metro al acta», P6b). A representative integer would put a
    // fabricated measurement into crack_width_mm, which the severity
    // derivation and the audit trail read as measured — so the band
    // travels as itself. The dashboard's numeric input keeps anchoMm.
    anchoBand: anchoBandSchema.nullable(),
})
    .partial()
    .strict();
/** What the prompt renders for a band — the model never sees a number
 *  the human did not measure. */
export const ANCHO_BAND_LABEL = {
    lt1: "menos de 1 mm",
    "1to3": "entre 1 y 3 mm",
    gt3: "más de 3 mm",
};
/**
 * Does a live crack_width_mm already sit inside the band the human
 * confirmed? /reclassify's «información nueva» gate: a band that merely
 * restates the live value is not new information. A null live width
 * never matches — the band IS new then. Ranges: lt1 [0,1), 1to3 [1,3],
 * gt3 (3,∞).
 */
export function anchoMmInBand(anchoMm, band) {
    if (anchoMm === null)
        return false;
    if (band === "lt1")
        return anchoMm < 1;
    if (band === "1to3")
        return anchoMm >= 1 && anchoMm <= 3;
    return anchoMm > 3;
}
/**
 * Why the evidence clamp did or did not lift on a reclassify — the
 * EXPLANATION axis the client renders on P9/P10/D3, orthogonal to
 * `motivo` (which describes the shape of the candidate set). Derived
 * here, once, from the attribution result; core-api renders the es-CL
 * label (`ATRIBUCION_LABEL`, same single-source posture as FALTA_LABEL)
 * and never re-derives the code. The wire enum derives from this const
 * (fitness: wire-enums-derive-from-engine).
 *
 * Three of the four states never lift the clamp — fail-closed by
 * construction. `verificada` is the only one that does, and it never
 * travels on a sub-threshold branch (reaching ≥ 0.75 requires it).
 */
export const RECLASSIFY_ATRIBUCION_CODES = [
    "verificada", // photoIndices ∩ verified ≠ ∅ — clamp inactive
    "no_citada", // new detalle photo(s) existed; the model attributed elsewhere
    "no_disponible", // the model omitted photoIndices (or cited only out of range)
    "sin_evidencia_nueva", // hints-only run: no unconsumed detalle photo was sent
];
export function clasificarAtribucion(policy, attribution, photoIndices) {
    if (attribution.attributed)
        return "verificada";
    if (policy.verifiedPhotoIndices.length === 0)
        return "sin_evidencia_nueva";
    if (!photoIndices || photoIndices.length === 0)
        return "no_disponible";
    return "no_citada";
}
// ---------------------------------------------------------------------------
// OTR escape hatch (spec invariant 9 — #0698)
/** The one especialidad outside the closed catalog: "Otro". Requires
 *  non-empty free text and enqueues the observation for human review —
 *  it flags, it never blocks emission on its own (founder 2026-09-01:
 *  «Sólo marca, no bloquea»; only a low confianza_ia also present
 *  retains emission). Spec-constant literal, like 'S1'..'S4'. */
export const OTR_ESPECIALIDAD = "OTR";
export const OTR_PARTIDA_SIN_CLASIFICAR = "OTR-01";
/**
 * Membership pre-check over a model proposal, fail-open by design: an
 * unknown code never fails the intake — an unknown especialidad falls
 * back to OTR (the catalog's own escape hatch, spec invariant 9), any
 * other unknown code drops to null and flips `categoriaProvisional`
 * where it applies. Conditional fields survive only with the tipo de
 * falla that declares them (spec §7).
 *
 * The confidence clamp (#0604) is applied here too:
 * `min(confianza, candidatos[0].confianza, evidencia ? 1.0 : 0.70)` —
 * a proposal without verified photo evidence can never assert more
 * than CONFIANZA_SIN_EVIDENCIA_MAX regardless of what the model says.
 *
 * También el ÚNICO implementador del contrato de procedencia (#0646):
 * la verificación de `citaRecinto` vive acá y en ningún otro lado, por
 * el mismo argumento que ya está escrito arriba de
 * `resolveEvidenceAttribution` — dos implementaciones de una regla así
 * de sutil derivan, y CI corre sobre el mock, así que la deriva se
 * despacharía en verde. `textos` es opcional: sin ellos no hay contra
 * qué verificar, y una fuente `declarado` sin textos degrada a
 * `inferido` en vez de creerle al modelo.
 */
export function sanitizeTaxonomyProposal(catalog, proposal, opts) {
    const especialidadEntry = catalog.especialidades.find((e) => e.codigo === proposal.codEspecialidad);
    let codEspecialidad;
    let codPartida;
    let categoriaProvisional;
    if (!especialidadEntry) {
        codEspecialidad = OTR_ESPECIALIDAD;
        codPartida = OTR_PARTIDA_SIN_CLASIFICAR;
        categoriaProvisional = true;
    }
    else {
        codEspecialidad = especialidadEntry.codigo;
        const partida = proposal.codPartida != null
            ? especialidadEntry.partidas.find((p) => p.codigo === proposal.codPartida)
            : undefined;
        codPartida = partida?.codigo ?? null;
        categoriaProvisional = partida === undefined;
    }
    const tipoFalla = proposal.codTipoFalla != null
        ? catalog.tiposFalla.find((t) => t.codigo === proposal.codTipoFalla)
        : undefined;
    const codTipoFalla = tipoFalla?.codigo ?? null;
    const aguaActiva = tipoFalla?.campoAdicional === "agua_activa" ? (proposal.aguaActiva ?? null) : null;
    const anchoMm = tipoFalla?.campoAdicional === "ancho_mm" &&
        typeof proposal.anchoMm === "number" &&
        proposal.anchoMm > 0
        ? proposal.anchoMm
        : null;
    const codRecinto = proposal.codRecinto != null && catalog.recintos.some((r) => r.codigo === proposal.codRecinto)
        ? proposal.codRecinto
        : null;
    const codElemento = proposal.codElemento != null && catalog.elementos.some((e) => e.codigo === proposal.codElemento)
        ? proposal.codElemento
        : null;
    // --- Procedencia por eje (#0646) -----------------------------------
    // Fail-closed en tres capas, en este orden:
    //   1. Un valor que no sea exactamente uno de los dos de FUENTES_EJE
    //      cae a null. Mismo patrón que la membresía de códigos: el modelo
    //      dio una respuesta usable cuya procedencia no es legible, y la
    //      política es degradar el ruteo, no abortar la llamada.
    //   2. Un eje cuyo código quedó en null no puede tener procedencia —
    //      declarar el origen de un valor que no existe es incoherente y
    //      dejaría al consumidor leyendo "declarado" sobre un NULL.
    //   3. `declarado` sobre el recinto sólo sobrevive si la cita literal
    //      aparece en alguno de los textos que el modelo recibió.
    const fuente = (v) => v === "declarado" || v === "inferido" ? v : null;
    /** La verificación de cita, en UNA implementación. La consumen los dos
     *  carriles —la fila única y el conjunto (#0646-ext)— y por el mismo
     *  argumento que ya justifica que el sanitizador sea el único
     *  implementador del contrato: dos copias de una regla así de sutil
     *  derivan, y CI corre sobre el mock, así que la deriva se
     *  despacharía en verde.
     *
     *  El piso de 3 evita que un fragmento como "el" matchee cualquier
     *  texto y vuelva la verificación decorativa; el techo de 200 evita
     *  que el modelo "cite" el transcript entero, que también matchearía
     *  siempre. Ambos miden sobre el texto normalizado, que es el mismo
     *  sobre el que se busca. El supervisor primero: es la voz dictada
     *  para ESTE remedio, mientras la nota base puede arrastrar texto de
     *  una corrida previa. */
    const verificarCita = (valor) => {
        const cruda = typeof valor === "string" ? valor.trim() : "";
        const cita = normalizarCita(cruda);
        if (cita.length < 3 || cita.length > 200)
            return null;
        if (normalizarCita(opts.textos?.supervisor ?? "").includes(cita)) {
            return { cita: cruda, origen: "supervisor" };
        }
        if (normalizarCita(opts.textos?.nota ?? "").includes(cita)) {
            return { cita: cruda, origen: "nota" };
        }
        return null;
    };
    // --- Conjunto de candidatos de recinto, capa 1 (#0646-ext) ---------
    // Membresía + dedupe PRESERVANDO EL ORDEN: el orden es el ranking del
    // modelo y es el orden en que la pantalla muestra las filas, así que
    // ordenar o reordenar acá cambiaría lo que el supervisor lee primero.
    // Se computa ANTES de la procedencia porque la procedencia depende de
    // él (ver la revocación declarativa más abajo).
    const vistos = new Set();
    const candidatosCrudos = [];
    for (const c of proposal.candidatosRecinto ?? []) {
        if (typeof c !== "string" || vistos.has(c))
            continue;
        if (!catalog.recintos.some((r) => r.codigo === c))
            continue;
        vistos.add(c);
        candidatosCrudos.push(c);
    }
    let fuenteRecinto = codRecinto === null ? null : fuente(proposal.fuenteEjes?.recinto);
    const fuenteElemento = codElemento === null ? null : fuente(proposal.fuenteEjes?.elemento);
    const fuenteTipoFalla = codTipoFalla === null ? null : fuente(proposal.fuenteEjes?.tipoFalla);
    // El agujero que el conjunto vuelve expresable y por lo tanto cerrable
    // (#0646-ext, decisión del founder). La cita verifica que el fragmento
    // EXISTA en el texto, no que DETERMINE una sola fila: hasta ahora un
    // modelo que devolviera codRecinto RC-05 + `declarado` + cita «es en el
    // baño» pasaba las tres capas y ese valor se escribía como confirmación
    // humana, sobre un enunciado que no distingue RC-05 de RC-06 ni de
    // RC-07. Ahora el propio modelo lo dice: si enumera más de un candidato
    // para el eje, está admitiendo que el enunciado no cierra una fila, y
    // ese eje PIERDE la procedencia declarativa. El código no se descarta
    // —puede seguir siendo correcto desde la foto—, se descarta la
    // afirmación, igual que cuando la cita no verifica.
    if (candidatosCrudos.length > 1 && fuenteRecinto === "declarado") {
        fuenteRecinto = "inferido";
    }
    let citaRecinto = null;
    let citaRecintoOrigen = null;
    if (fuenteRecinto === "declarado") {
        const verificada = verificarCita(proposal.citaRecinto);
        if (verificada) {
            citaRecinto = verificada.cita;
            citaRecintoOrigen = verificada.origen;
        }
        else {
            // No se descarta el código: puede seguir siendo correcto desde la
            // foto. Lo que se descarta es la AFIRMACIÓN DE PROCEDENCIA, que es
            // lo único que el carril declarativo consume.
            fuenteRecinto = "inferido";
        }
    }
    // --- Conjunto de candidatos de recinto, capas 2 a 5 (#0646-ext) ----
    // Todas fail-closed y ninguna lanza, misma doctrina que la membresía
    // de códigos: el modelo dio una respuesta usable cuyo conjunto no es
    // ofrecible, y la política es degradar el ruteo, no abortar la
    // llamada.
    //
    //  2. EXCLUSIVIDAD — el conjunto sobrevive sólo si `codRecinto` quedó
    //     en null. `candidatos` de especialidad sí coexiste con
    //     `codEspecialidad` porque ahí el código nunca es null y el
    //     arreglo mide dispersión de confianza; acá el conjunto existe
    //     para REEMPLAZAR un null. Si coexistieran, el cliente ofrecería
    //     un picker sobre un eje ya resuelto — que es lo que la Guarda 2
    //     de ADR-0122 §6 rechaza server-side con `eje_ya_resuelto`.
    //  3. CARDINALIDAD 2..3. Piso de 2: un conjunto de uno es una fila que
    //     el modelo se negó a escribir, y «ofrecer elegí con una sola
    //     opción es el mismo clic de trámite» (ADR-0122). Techo de 3: un
    //     conjunto de 4+ no es un grupo, es un encogimiento de hombros, y
    //     `ux-expert` retiró el picker en #0627 por fricción real.
    //  4. Conjunto vacío ⇒ fuente null. Espejo de la capa que ya fuerza a
    //     null la procedencia de un eje cuyo código quedó en null.
    //  5. La cita también candea el conjunto: con `declarado`, la MISMA
    //     verificación; si no matchea, degrada a `inferido` y el conjunto
    //     SOBREVIVE — lo que se descarta es la afirmación de procedencia,
    //     no las filas que el supervisor todavía puede elegir.
    const candidatosRecinto = codRecinto !== null || candidatosCrudos.length < 2 || candidatosCrudos.length > 3
        ? []
        : candidatosCrudos;
    let fuenteCandidatosRecinto = candidatosRecinto.length === 0 ? null : fuente(proposal.fuenteCandidatosRecinto);
    if (fuenteCandidatosRecinto === "declarado") {
        const verificada = verificarCita(proposal.citaRecinto);
        if (verificada) {
            // La cita del conjunto es la MISMA cita: el fragmento que nombra
            // el grupo. Se publica para que la pantalla pueda citarle al
            // supervisor sus propias palabras («Dijiste "en el baño" —
            // ¿cuál?») en vez de una corazonada de la máquina.
            citaRecinto = verificada.cita;
            citaRecintoOrigen = verificada.origen;
        }
        else {
            fuenteCandidatosRecinto = "inferido";
        }
    }
    const clamp01 = (n) => Math.min(1, Math.max(0, n));
    const candidatos = (proposal.candidatos ?? [])
        .filter((c) => c.codEspecialidad === OTR_ESPECIALIDAD ||
        catalog.especialidades.some((e) => e.codigo === c.codEspecialidad))
        .map((c) => ({ codEspecialidad: c.codEspecialidad, confianza: clamp01(c.confianza) }))
        .sort((a, b) => b.confianza - a.confianza)
        .slice(0, 3);
    // --- Conjunto de candidatos de partida (decisión 6) ----------------
    // LAS MISMAS CAPAS que el conjunto de recinto, y por las mismas
    // razones: membresía contra el catálogo, deduplicación PRESERVANDO EL
    // ORDEN —el orden es el ranking del modelo y es el orden en que la
    // pantalla lo muestra— y descarte silencioso de lo que no sobrevive.
    // Fail-open y sin lanzar, igual que la membresía de códigos: el modelo
    // dio una respuesta usable cuyo conjunto está sucio, y la política es
    // degradar el ruteo, no abortar la llamada.
    //
    // Dos diferencias deliberadas con `candidatosRecinto`:
    //   - NO hay exclusividad contra `codPartida`, ni piso de 2. Ese
    //     conjunto existe para REEMPLAZAR un null y por eso muere cuando el
    //     eje ya está resuelto; éste convive con la fila escrita porque su
    //     trabajo es dejar CORREGIRLA con un tap, igual que `candidatos` de
    //     especialidad convive con `codEspecialidad`.
    //   - El exceso TRUNCA en vez de tumbar el conjunto entero (mismo
    //     `.slice(0, 3)` que `candidatos`): un cuarto candidato acá no es la
    //     confesión de ambigüedad que un cuarto recinto sí es — el ranking
    //     sigue siendo bueno y tirar las tres primeras filas por culpa de la
    //     cuarta dejaría el bottom sheet vacío sobre un catálogo de ~80.
    const partidasVistas = new Set();
    const candidatosPartida = [];
    for (const c of proposal.candidatosPartida ?? []) {
        if (typeof c !== "string" || partidasVistas.has(c))
            continue;
        if (!findTaxonomyPartida(catalog, c))
            continue;
        partidasVistas.add(c);
        candidatosPartida.push(c);
    }
    const confianza = clamp01(Math.min(proposal.confianza, candidatos[0]?.confianza ?? proposal.confianza, opts.evidenciaVerificada ? 1 : CONFIANZA_SIN_EVIDENCIA_MAX));
    return {
        codRecinto,
        codElemento,
        codEspecialidad,
        codPartida,
        categoriaProvisional,
        codTipoFalla,
        aguaActiva,
        anchoMm,
        p1Risk: proposal.p1Risk,
        p2FunctionOrProgression: proposal.p2FunctionOrProgression,
        p3MinorFunctional: proposal.p3MinorFunctional,
        confianza,
        candidatos,
        fuenteEjes: {
            recinto: fuenteRecinto,
            elemento: fuenteElemento,
            tipoFalla: fuenteTipoFalla,
        },
        citaRecinto,
        citaRecintoOrigen,
        candidatosRecinto,
        fuenteCandidatosRecinto,
        candidatosPartida: candidatosPartida.slice(0, 3),
    };
}
/**
 * Normalización de comparación para la verificación de `citaRecinto`
 * (#0646): minúsculas, NFD sin diacríticos, espacios colapsados.
 *
 * Se normalizan LOS DOS LADOS con la misma función. El modelo transcribe
 * en un registro y el dictado llega en otro —«Living» vs «livin», «baño»
 * vs «bano»— y una comparación literal convertiría cada diferencia de
 * tipografía en una degradación a `inferido`. Lo que NO se relaja es la
 * literalidad léxica: una paráfrasis sigue sin matchear, que es
 * exactamente el hábito que el candado existe para detectar.
 */
function normalizarCita(s) {
    return s
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, " ")
        .trim();
}
// ---------------------------------------------------------------------------
// ADR-0163 (#0770) — el título de una observación que nació de una consulta
/** Tope del título (el mismo de `ai_title`, que ai-svc recorta a 80). */
export const TAXONOMY_OBSERVATION_TITLE_MAX = 80;
/**
 * El título de una observación armado SOLO desde su clasificación:
 * «{Tipo de falla} en {elemento} · {Recinto}», p. ej. «Humedad, filtración o
 * fuga en muro · Baño principal». Recortado a 80 con «…».
 *
 * Existe para la consulta convertida: su título no puede salir del texto del
 * propietario (viaja al contratista por el enlace y el correo, y la regla es
 * que al contratista le llega sólo lo que escribe el jefe) ni de lo que
 * redactó el modelo (parafrasea esas mismas palabras). Sólo nombres del
 * catálogo, nunca texto libre. Total: un código desconocido o ausente se
 * omite; sin tipo de falla cae al elemento, y sin nada a «Observación».
 */
export function taxonomyObservationTitle(catalog, codes) {
    const tipo = catalog.tiposFalla.find((t) => t.codigo === codes.codTipoFalla)?.nombre;
    const elemento = catalog.elementos.find((e) => e.codigo === codes.codElemento)?.nombre;
    const recinto = catalog.recintos.find((r) => r.codigo === codes.codRecinto)?.nombre;
    const que = tipo && elemento
        ? `${tipo} en ${elemento.toLocaleLowerCase("es-CL")}`
        : (tipo ?? elemento ?? "Observación");
    const full = recinto ? `${que} · ${recinto}` : que;
    return full.length <= TAXONOMY_OBSERVATION_TITLE_MAX
        ? full
        : `${full.slice(0, TAXONOMY_OBSERVATION_TITLE_MAX - 1).trimEnd()}…`;
}
//# sourceMappingURL=taxonomy.js.map